// src/callbacks.js
// ═══════════════════════════════════════════════════════════════
// CALLS TO RETURN — missed calls and voicemails not called back yet (10/10/26)
// ═══════════════════════════════════════════════════════════════
// Reece, 10/10: "I like the daily call list for missed calls and voicemails
// not yet returned, and how long. I don't like the idea of follow-ups and
// things like that outside of a whiteboard style … It feels a bit wasted and
// cluttered to have unnecessary reminders." So this is ONLY that list — no
// bell, badge, notification, reminder, follow-up date or nag anywhere.
//
// The source of truth is RingCentral's own call log for his extension (every
// device on it — desk phone, RC app, mobile — not just a browser that was
// open), read by src/ringcentral.js fetchCallLog. This module is the RULES,
// pure (no React, no network, no storage):
//   normalizeCall   one call-log record → {at, direction, from, to, result, voicemail…}
//   kindOf          missed | answered | blocked | outbound | in-progress | internal | other
//   buildCallbacks  records + Done marks → ONE row per caller number still
//                   waiting, longest wait first; hidden rows apart; counts
//   formatWait      "38 m" · "3 h 12 m" · "2 d 4 h"
//   readGate        the 60-second floor + RingCentral's Retry-After
//   doneRow / doneMarksFrom / doneCutoff   his "Done" marks (contact_log rows)
//   linkCallers     a number → the pipeline file / lead it belongs to
//                   (contactLog's matcher — a skip-traced number may say who
//                   it is; it is never copied onto the lead)
//
// THE RULES (change them only with a test — src/__tests__/callbacks.test.js):
//   • MISSED = an Inbound call with a caller number whose result is NOT an
//     answered value (Accepted / Call connected), NOT In Progress and NOT
//     Blocked. Turned around on purpose (the 10/10 review): RingCentral has
//     ~50 results — Missed, Voicemail, Rejected, Hang Up, No Answer, Busy,
//     Declined, Abandoned… — and an allow-list of three would quietly drop a
//     declined caller or one who hung up during the greeting. BLOCKED (his
//     own block list turned them away — RingCentral sends those unless asked
//     not to) is never a call to return: counted, no row, and it doesn't
//     return an earlier miss either. `resultsByDirection` (counts only,
//     inbound and outbound apart — the same words mean different things on
//     each side) lets the mapping be checked against what his account
//     produces.
//   • VOICEMAIL = result Voicemail / Sent to Voicemail, or a voicemail message
//     attached to the record.
//   • No caller number (an internal extension, a blocked caller ID) → no row,
//     only counted.
//   • RETURNED (the row goes) = any of these AFTER the missed call:
//       an Outbound call from his line to that number, whatever its result
//       (reaching their voicemail counts — he tried);
//       a later Inbound call from them that was answered;
//       his Done mark — which clears only the misses the row SHOWED (its
//       newest miss is the mark's time, `doneCutoff`), never one that came
//       in after the list was read.
//     A text reply doesn't count (Done covers it).
//   • ONE ROW PER NUMBER: the oldest missed call not returned sets "waiting
//     since"; tries = the missed calls since the last return; voicemail when
//     any of them left one.
//   • HIDDEN by default (a "show N hidden" toggle): his own numbers, toll-free
//     numbers and vendor / lender caller IDs — contactLog's rules, one copy.

import { OWN_NUMBERS, buildCrmIndex, matchPerson, isVendorCallerId, isTollFreeNumber, isGenericCallerId, stateOfAreaCode } from './contactLog';

export const CALLBACK_LOOKBACK_DAYS = 14;
// Never more than one call-log read a minute (RingCentral's "Heavy" group is
// 10 a minute per user per app; polling the call log is not a supported use).
export const CALLBACK_MIN_READ_MS = 60000;
// A finished call takes 15–30 s to show up in the call log.
export const CALLBACK_AFTER_HANGUP_MS = 45000;
// Done marks are read back this far (a day past the call-log window).
export const CALLBACK_DONE_DAYS = CALLBACK_LOOKBACK_DAYS + 1;
export const DONE_KIND = "callback-done";
export const DONE_REASONS = ["Texted", "Spam", "Vendor"];
export const HIDDEN_LABEL = { own: "your own number", "toll-free": "toll-free", vendor: "vendor / lender caller ID" };
export const PERMISSION_LINE = "Turn on Read Call Log for your RingCentral app, then Disconnect / Connect in Settings";
export const ALL_RETURNED = "All calls returned";

const ANSWERED = new Set(["accepted", "call connected"]);
const IN_PROGRESS = new Set(["in progress"]);
const BLOCKED = new Set(["blocked"]);
const VOICEMAIL = new Set(["voicemail", "sent to voicemail"]);
const DAY = 864e5;

// ═══════ numbers ═══════
// The caller's number as a key: US numbers as their 10 digits (+1 dropped);
// anything longer kept whole (an international caller); under 7 digits is
// an extension, not a caller → "".
export function numberKey(raw) {
  let d = String(raw == null ? "" : raw).replace(/\D/g, "");
  if (d.length === 11 && d.charAt(0) === "1") d = d.slice(1);
  return d.length >= 7 ? d : "";
}
export function formatNumber(key) {
  const d = String(key || "");
  if (d.length === 10) return "(" + d.slice(0, 3) + ") " + d.slice(3, 6) + "-" + d.slice(6);
  return d ? "+" + d : "";
}
// His own numbers: the known line (contactLog.OWN_NUMBERS — 302-893-8949) +
// Settings' admin phone + the RingCentral SMS-from number.
export function ownNumbers(settings) {
  const s = new Set(OWN_NUMBERS);
  const st = settings || {};
  [st.adminPhone, st.rcSmsFrom].forEach(function (p) { const k = numberKey(p); if (k) s.add(k); });
  return s;
}

// ═══════ one record ═══════
// A RingCentral call-log record (Simple or Detailed view: startTime,
// direction, result, from / to {phoneNumber, extensionNumber, name},
// message {type:"VoiceMail"}…) → the fields the rules read; null when it has
// no usable time.
export function normalizeCall(r) {
  if (!r || typeof r !== "object") return null;
  const at = Date.parse(String(r.startTime || ""));
  if (!isFinite(at)) return null;
  const dir = String(r.direction || "");
  const from = r.from || {}, to = r.to || {};
  const result = String(r.result == null ? "" : r.result).trim();
  const msg = r.message || null;
  return {
    id: String(r.id || r.sessionId || r.telephonySessionId || at),
    at: at,
    direction: /^in/i.test(dir) ? "in" : /^out/i.test(dir) ? "out" : "",
    from: numberKey(from.phoneNumber),
    fromName: String(from.name || "").trim(),
    to: numberKey(to.phoneNumber),
    result: result,
    voicemail: VOICEMAIL.has(result.toLowerCase()) || !!(msg && /voice/i.test(String(msg.type || ""))),
  };
}
export function kindOf(c) {
  if (!c) return "other";
  if (c.direction === "out") return c.to ? "outbound" : "internal";
  if (c.direction !== "in") return "other";
  if (!c.from) return "internal";
  const r = String(c.result || "").toLowerCase();
  if (IN_PROGRESS.has(r)) return "in-progress";
  if (ANSWERED.has(r)) return "answered";
  if (BLOCKED.has(r)) return "blocked";
  return "missed";
}
// Result values seen, COUNTS ONLY (never a number or a name) — what his
// account actually records, so the missed / answered mapping can be checked.
export function resultCounts(records) {
  const out = {};
  (records || []).forEach(function (r) {
    const k = String((r && r.result) == null ? "" : r.result).trim() || "(none)";
    out[k] = (out[k] || 0) + 1;
  });
  return out;
}
// The same, inbound and outbound apart (his own calls say "No Answer" /
// "Busy" / "Hang Up" too — mixed in, they'd read as missed-call values).
// A record with no direction lands in `other`. → {inbound, outbound, other}
export function resultsByDirection(records) {
  const by = { inbound: [], outbound: [], other: [] };
  (records || []).forEach(function (r) {
    const d = String((r && r.direction) || "");
    (/^in/i.test(d) ? by.inbound : /^out/i.test(d) ? by.outbound : by.other).push(r);
  });
  return { inbound: resultCounts(by.inbound), outbound: resultCounts(by.outbound), other: resultCounts(by.other) };
}
export function hiddenReason(number, names, own) {
  if ((own || ownNumbers()).has(number)) return "own";
  if (isTollFreeNumber(number)) return "toll-free";
  if ((names || []).some(function (n) { return isVendorCallerId(n); })) return "vendor";
  return null;
}

// ═══════ the list ═══════
// records: the call log as RingCentral returned it. opts: {own: Set, done:
// Map number → the latest Done mark (ms — the newest miss it covers), now}.
// → {rows (still waiting, longest wait first), hidden (same shape, `hidden`
// set), counts, results ({inbound, outbound, other} — counts only)}.
export function buildCallbacks(records, opts) {
  const o = opts || {};
  const own = o.own || ownNumbers();
  const done = o.done || new Map();
  const now = o.now != null ? o.now : Date.now();
  const counts = { records: 0, missed: 0, voicemail: 0, answered: 0, blocked: 0, outbound: 0, internal: 0, inProgress: 0, other: 0, unreadable: 0 };
  const by = new Map();
  const group = function (n) { let g = by.get(n); if (!g) { g = { misses: [], clearedAt: 0, names: [] }; by.set(n, g); } return g; };
  (records || []).forEach(function (r) {
    counts.records++;
    const c = normalizeCall(r);
    if (!c) { counts.unreadable++; return; }
    const k = kindOf(c);
    if (k === "missed") {
      counts.missed++; if (c.voicemail) counts.voicemail++;
      const g = group(c.from); g.misses.push(c); if (c.fromName) g.names.push({ at: c.at, name: c.fromName });
    } else if (k === "answered") {
      counts.answered++;
      const g = group(c.from); g.clearedAt = Math.max(g.clearedAt, c.at); if (c.fromName) g.names.push({ at: c.at, name: c.fromName });
    } else if (k === "outbound") {
      counts.outbound++;
      const g = group(c.to); g.clearedAt = Math.max(g.clearedAt, c.at);
    } else if (k === "blocked") counts.blocked++;
    else if (k === "internal") counts.internal++;
    else if (k === "in-progress") counts.inProgress++;
    else counts.other++;
  });
  const rows = [], hidden = [];
  by.forEach(function (g, n) {
    const cleared = Math.max(g.clearedAt, Number(done.get(n)) || 0);
    const open = g.misses.filter(function (m) { return m.at > cleared; }).sort(function (a, b) { return a.at - b.at; });
    if (!open.length) return;
    const names = g.names.slice().sort(function (a, b) { return b.at - a.at; }).map(function (x) { return x.name; });
    const shown = names.find(function (x) { return !isGenericCallerId(x); }) || "";
    const vm = open.filter(function (m) { return m.voicemail; }).length;
    const row = {
      number: n, display: formatNumber(n), callerName: shown,
      firstMissAt: open[0].at, lastMissAt: open[open.length - 1].at,
      tries: open.length, voicemails: vm, voicemail: vm > 0,
      waitMs: Math.max(0, now - open[0].at),
      hidden: hiddenReason(n, names, own),
    };
    (row.hidden ? hidden : rows).push(row);
  });
  const order = function (a, b) { return a.firstMissAt - b.firstMissAt || (a.number < b.number ? -1 : 1); };
  rows.sort(order); hidden.sort(order);
  return { rows: rows, hidden: hidden, counts: counts, results: resultsByDirection(records) };
}

// ═══════ how long ═══════
export function formatWait(ms) {
  const m = Math.max(0, Math.floor((Number(ms) || 0) / 60000));
  if (m < 1) return "under 1 m";
  if (m < 60) return m + " m";
  const h = Math.floor(m / 60), mm = m % 60;
  if (h < 24) return h + " h" + (mm ? " " + mm + " m" : "");
  const d = Math.floor(h / 24), hh = h % 24;
  return d + " d" + (hh ? " " + hh + " h" : "");
}
// "" · "long" (4 h+) · "day" (24 h+) — the wait's colour only, never a badge.
export function waitLevel(ms) { return ms >= DAY ? "day" : ms >= 4 * 36e5 ? "long" : ""; }
export function lookbackFrom(now, days) { return new Date((now != null ? now : Date.now()) - (days || CALLBACK_LOOKBACK_DAYS) * DAY).toISOString(); }

// ═══════ when a read may happen ═══════
// state {lastTryAt, blockedUntil} → {ok} or {ok:false, waitMs, why: "floor" |
// "retry-after"}. Reads happen only when the list opens, on Refresh and after
// a hang-up — this only says whether one may happen NOW.
export function readGate(state, now) {
  const s = state || {};
  const t = now != null ? now : Date.now();
  const block = Math.max(0, (Number(s.blockedUntil) || 0) - t);
  const floor = s.lastTryAt ? Math.max(0, Number(s.lastTryAt) + CALLBACK_MIN_READ_MS - t) : 0;
  const wait = Math.max(block, floor);
  if (wait <= 0) return { ok: true, waitMs: 0, why: "" };
  return { ok: false, waitMs: wait, why: block >= floor ? "retry-after" : "floor" };
}
// Is there a call log to read at all? (Reece's RingCentral, on and connected.)
export function callLogUsable(settings, rcAuthed) {
  return !!(settings && settings.rcEnabled && rcAuthed);
}

// ═══════ Done marks ═══════
// One contact_log row per Done (kind "callback-done") — readable in the
// person's New Call timeline: "Call back done — Texted" / "— no reason".
// Its `at` is the CUTOFF — the newest miss the row showed when he tapped
// Done — never "now": the list is only as fresh as its last read, and a
// miss / voicemail that came in after that read must stay on the list
// (review 10/10). The timeline shows when he tapped it (created_at).
// row (a list row) → ms; no miss time on it → the read it came from, else now.
export function doneCutoff(row, readAt, now) {
  const last = Number(row && row.lastMissAt);
  if (isFinite(last) && last > 0) return last;
  const r = Number(readAt);
  if (isFinite(r) && r > 0) return r;
  return now != null ? now : Date.now();
}
export function doneBody(reason) {
  const r = String(reason == null ? "" : reason).trim();
  return "Call back done — " + (r || "no reason");
}
export function doneRow(o) {
  const x = o || {};
  const r = String(x.reason == null ? "" : x.reason).trim();
  return {
    kind: DONE_KIND, phone: x.number || "", at: x.at || new Date().toISOString(),
    body: doneBody(r), reason: r || null,
    lead_id: x.leadId || null, loan_id: x.loanId || null, borrower_name: x.name || "",
  };
}
// contact_log rows + this device's marks ({number: iso}) + this session's
// ({number: ms}) → Map number → the latest Done (ms — each mark's cutoff).
export function doneMarksFrom(rows, local, session) {
  const out = new Map();
  const put = function (n, t) { const k = numberKey(n); const ms = typeof t === "number" ? t : Date.parse(String(t || "")); if (!k || !isFinite(ms)) return; if (!out.has(k) || out.get(k) < ms) out.set(k, ms); };
  (rows || []).forEach(function (r) { if (r && (!r.kind || r.kind === DONE_KIND)) put(r.phone, r.at); });
  Object.keys(local || {}).forEach(function (n) { put(n, local[n]); });
  Object.keys(session || {}).forEach(function (n) { put(n, session[n]); });
  return out;
}

// ═══════ who it is ═══════
// numbers → Map number → {kind: "loan"|"lead"|"many", loan, lead, via,
// candidates} (no entry = nobody). contactLog's matcher, fed only the records
// that can match these numbers (a few, not 40K leads): a file's / lead's
// phone, a skip-traced number (outbound_contacts phone_digits → its lead —
// matched against, never copied onto the lead). A file and the lead it came
// from are one person; the FILE is opened.
export function linkCallers(numbers, crm) {
  const want = new Set((numbers || []).filter(Boolean));
  const out = new Map();
  if (!want.size) return out;
  const c = crm || {};
  const loans = (c.loans || []).filter(function (l) { return l && l.id; });
  const outbound = (c.outbound || []).filter(function (o) {
    return o && o.lead_id && (o.phone_digits || []).concat((o.phones || []).map(function (x) { return x && x.n; })).some(function (p) { return want.has(numberKey(p)); });
  });
  const leadIds = new Set();
  loans.forEach(function (l) { if (want.has(numberKey(l.ph)) && l.sourceLeadId) leadIds.add(l.sourceLeadId); });
  outbound.forEach(function (o) { leadIds.add(o.lead_id); });
  const leads = (c.leads || []).filter(function (l) { return l && l.id && (want.has(numberKey(l.phone)) || leadIds.has(l.id)); });
  const ix = buildCrmIndex({ leads: leads, loans: loans, outbound: outbound });
  want.forEach(function (n) {
    const m = matchPerson({ phones: [n] }, ix);
    if (m.match === "sure") {
      const loan = m.loanId ? ix.loanById.get(m.loanId) || null : null;
      const lead = m.leadId ? ix.leadById.get(m.leadId) || null : null;
      if (loan || lead) out.set(n, { kind: loan ? "loan" : "lead", loan: loan, lead: lead, via: m.via || "" });
    } else if (m.match === "likely" && m.candidates && m.candidates.length) {
      out.set(n, { kind: "many", loan: null, lead: null, via: m.via || "", candidates: m.candidates });
    }
  });
  return out;
}
// What the row says it is: the file (name · file id · stage), the lead, more
// than one record, the RingCentral caller ID, else "Unknown caller · <state>".
export function callerLabel(row, link) {
  const l = link || null;
  if (l && l.kind === "loan" && l.loan) return { title: l.loan.b || "(no name)", sub: "file " + l.loan.id + (l.loan.stage ? " · " + l.loan.stage : ""), known: true };
  if (l && l.kind === "lead" && l.lead) return { title: l.lead.name || "(no name)", sub: "lead" + (/skip-traced/.test(l.via || "") ? " · skip-traced number" : ""), known: true };
  if (l && l.kind === "many") return { title: (row && row.callerName) || "More than one record", sub: (l.candidates || []).length + " records have this number", known: false };
  if (row && row.callerName) return { title: row.callerName, sub: "RingCentral caller ID", known: false };
  const st = stateOfAreaCode(row && row.number);
  return { title: "Unknown caller" + (st ? " · " + st : ""), sub: "", known: false };
}
